import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Seo } from "../components/site/Seo";
import { Reveal, ImageReveal, MaskLines } from "../lib/motion";
import { CTASection } from "../components/site/CTASection";
import { getArticle, ARTICLES, formatDate } from "../data/journal";

export default function JournalDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/journal" replace />;
  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <>
      <Seo title={`${article.title} | Arc & Hollow Journal`} description={article.excerpt} path={`/journal/${slug}`} noindex />

      <article data-testid="journal-detail">
        <header className="container-x pt-36 md:pt-52 pb-12">
          <Link to="/journal" className="inline-flex items-center gap-2 overline text-olive hover:text-charcoal mb-10" data-testid="back-to-journal">
            <ArrowLeft size={14} strokeWidth={1.6} /> Journal
          </Link>
          <p className="overline text-bronze mb-8">{article.category} · {article.readingTime} · {formatDate(article.date)}</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            <MaskLines lines={[article.title]} />
          </h1>
          <p className="mt-8 max-w-2xl text-xl font-light leading-relaxed text-olive">{article.intro}</p>
        </header>

        <div className="container-x pb-8">
          <ImageReveal src={article.hero} alt={article.title} className="aspect-[16/9]" />
        </div>

        <div className="container-x py-12 md:py-16">
          <div className="max-w-2xl mx-auto">
            {article.body.slice(0, 1).map((p, i) => (
              <Reveal key={i}><p className="text-lg font-light leading-loose text-charcoal/85 mb-8">{p}</p></Reveal>
            ))}

            <Reveal>
              <blockquote className="my-12 border-l-2 border-bronze pl-8">
                <p className="font-serif text-3xl md:text-4xl font-light italic leading-snug text-charcoal">
                  “{article.pullQuote}”
                </p>
              </blockquote>
            </Reveal>

            <Reveal>
              <figure className="my-12">
                <ImageReveal src={article.inlineImage} alt={article.inlineCaption} className="aspect-[3/2]" />
                <figcaption className="mt-3 text-xs uppercase tracking-overline text-olive/70">{article.inlineCaption}</figcaption>
              </figure>
            </Reveal>

            {article.body.slice(1).map((p, i) => (
              <Reveal key={i}><p className="text-lg font-light leading-loose text-charcoal/85 mb-8">{p}</p></Reveal>
            ))}
          </div>
        </div>

        {related.length > 0 && (
          <section className="container-x py-16 md:py-24 border-t border-charcoal/10">
            <p className="overline text-bronze mb-10">Related entries</p>
            <div className="grid gap-x-8 gap-y-12 md:grid-cols-2">
              {related.map((a) => (
                <Link key={a.slug} to={`/journal/${a.slug}`} className="group block" data-testid={`related-journal-${a.slug}`}>
                  <ImageReveal src={a.hero} alt={a.title} className="aspect-[4/3]" imgClassName="transition-transform duration-[1200ms] group-hover:scale-105" />
                  <p className="mt-4 overline text-bronze">{a.category}</p>
                  <h3 className="mt-2 font-serif text-2xl font-light group-hover:text-bronze transition-colors duration-500">{a.title}</h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      <CTASection />
    </>
  );
}
